import React, { useState } from "react";
import "../css/ProfilePage.css";

import { useNavigate } from "react-router-dom";
import useAuthStore from "../store/authstore";
import { useFrappeGetDoc, useFrappePutCall } from "frappe-react-sdk";
import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close, Logout } from "@mui/icons-material";
import toast from "react-hot-toast";
import { getInitials, throwErrorMsg } from "../utils/common.util";
import Loader from "../Components/Loader";

const ICONS = {
  arrowLeft: (
    <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M20 21a8 8 0 1 0-16 0" />
    </>
  ),
  lock: (
    <>
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </>
  ),
  bell: <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />,
  palette: (
    <path d="M12 2a10 10 0 0 0 0 20 1.5 1.5 0 0 0 1.5-1.5c0-.4.2-.7.5-.9.3-.2.7-.3 1.1-.3h.9a2 2 0 0 0 2-2c0-1.1-.9-2-2-2h-1a1 1 0 0 1-1-1V8a2 2 0 0 0-2-2h-1a2 2 0 0 0-2 2v1" />
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10" />
    </>
  ),
  logout: <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />,
  chevronRight: (
    <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
  ),
};

const SvgIcon = ({ name, size = 20, color = "currentColor" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    className="icon"
  >
    {ICONS[name]}
  </svg>
);

export default function ProfilePage() {
  const navigate = useNavigate();
  const logout = useAuthStore((state) => state.logout);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const handleLogoutClick = () => {
    setConfirmOpen(true);
  };

  // 3️⃣ Confirm logout
  const handleConfirmLogout = () => {
    setConfirmOpen(false);
    logout();
    navigate("/", { replace: true });
  };
  const handleCancelLogout = () => setConfirmOpen(false);
  const userDetails = useAuthStore((state) => state.user);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({});

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const {
    data: employeeDetails,
    mutate: employeeDataMutate,
    isLoading: employeeLoading,
  } = useFrappeGetDoc("User", userDetails.message, undefined, {
    shouldRetryOnError: false,
    revalidateOnMount: !!userDetails.message,
    revalidateOnFocus: false,
    revalidateIfStale: !!userDetails.message,
    onSuccess: (data) => {
      setFormData({
        first_name: data.first_name || "",
        last_name: data.last_name || "",
        email: data.email || "",
        phone: data.phone || "",
        gender: data.gender || "",
        birth_date: data.birth_date || null,
      });
    },
  });
  const MENU_GROUPS = [
    {
      title: "Account",
      items: [
        {
          icon: "user",
          title: "Personal Info",
          subtitle: "Name, email, phone",
          onClick: () => setIsModalOpen(true),
        },
        // { icon: "lock", title: "Security", subtitle: "Password, 2FA" },
      ],
    },
  ];
  const {
    call: callPut,

    loading: updateLoading,
  } = useFrappePutCall(
    /** method **/
    "frappe.client.set_value",
  );
  const updateUser = async () => {
    if (!formData.first_name || !formData.last_name || !formData.email) {
      toast.error("First name, last name and email are required.");
      return;
    }
    try {
      const res = await callPut({
        doctype: "User",
        name: userDetails.message,
        fieldname: { ...formData },
      });
      // Show success toast if API call is successful
      if (res.message) {
        setIsModalOpen(false);
        employeeDataMutate(); // Refresh user data
        toast.success("Profile updated successfully!");
      }
    } catch (err) {
      throwErrorMsg(err);
    }
  };

  return (
    <div className="profile-page">
      {employeeLoading || updateLoading ? <Loader /> : null}
      {/* Header */}
      <div className="detail-top-bar">
        <div className="detail-back-btn" onClick={() => navigate(-1)}>
          <svg width={8} height={13} viewBox="0 0 8 13" fill="none">
            <path
              d="M7 1L2 6.5L7 12"
              stroke="#2d7a4a"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>{" "}
          Profile
        </div>
      </div>

      {/* Profile Hero */}
      <section className="profile-hero">
        <div className="avatar">
          {" "}
          {getInitials(userDetails?.message || "NA")}
        </div>
        <h2 className="user-name">{employeeDetails?.full_name}</h2>
        {/* <p className="user-role">{user.role}</p> */}
        <p className="user-email">{employeeDetails?.email}</p>
      </section>

      {/* Stats Grid */}
      {/* <section className="stats-grid">
        {STATS.map((stat, i) => (
          <div key={i} className="stat-card">
            <span className="stat-value">{stat.value}</span>
            <span className="stat-label">{stat.label}</span>
          </div>
        ))}
      </section> */}

      {/* Menu Sections */}
      <div className="menu-wrapper">
        {MENU_GROUPS.map((group, gIdx) => (
          <div key={gIdx} className="menu-section">
            <h3 className="section-title">{group.title}</h3>
            <div className="menu-card">
              {group.items.map((item, iIdx) => (
                <button key={iIdx} className="menu-item" onClick={item.onClick}>
                  <div className="menu-icon">
                    <SvgIcon name={item.icon} />
                  </div>
                  <div className="menu-info">
                    <span className="menu-title">{item.title}</span>
                    {item.subtitle && (
                      <span className="menu-subtitle">{item.subtitle}</span>
                    )}
                  </div>
                  <SvgIcon name="chevronRight" size={16} color="#999" />
                </button>
              ))}
            </div>
          </div>
        ))}

        {/* Logout */}
        <div className="menu-section">
          <div className="menu-card">
            <button className="menu-item danger" onClick={handleLogoutClick}>
              <div className="menu-icon danger">
                <Logout />
              </div>
              <div className="menu-info">
                <span className="menu-title">Log Out</span>
              </div>
              <SvgIcon name="chevronRight" size={16} color="#d32f2f" />
            </button>
          </div>
        </div>
      </div>
      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: isMobile ? "90%" : 400,
            maxHeight: "100vh",
            bgcolor: "background.paper",
            borderRadius: isMobile ? "14px 14px 0 0" : "14px",
            outline: "none",
            overflow: "hidden",
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2,
              borderBottom: `1px solid ${alpha("#000", 0.08)}`,
            }}
          >
            <Typography variant="h6" fontWeight={700}>
              Personal Information
            </Typography>
            <IconButton onClick={() => setIsModalOpen(false)}>
              <Close />
            </IconButton>
          </Box>

          {/* Modal Form */}
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              updateUser();
            }}
            sx={{
              p: 2,
              overflowY: "auto",
              maxHeight: "calc(95vh - 140px)",
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="First Name"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              size="small"
              label="Last Name"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              size="small"
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              margin="normal"
              required
              disabled
            />
            <TextField
              fullWidth
              size="small"
              label="Phone Number"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              margin="normal"
            />
            <FormControl fullWidth margin="normal" size="small">
              <InputLabel id="gender-label">Gender</InputLabel>
              <Select
                labelId="gender-label"
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                label="Gender"
              >
                <MenuItem value="">Select gender</MenuItem>
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>

                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              placeholder="Birth Date"
              name="birth_date"
              type="date"
              value={formData.birth_date}
              onChange={handleInputChange}
              margin="normal"
              size="small"
            />

            {/* Modal Actions */}
            <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setIsModalOpen(false)}
                sx={{
                  borderColor: "#000000",
                  color: "#000000",
                  textTransform: "capitalize",
                }}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                variant="contained"
                type="submit"
                sx={{
                  background: "#1d5c34",
                  color: "white",
                  textTransform: "capitalize",
                }}
                disabled={updateLoading}
              >
                Save
              </Button>
            </Stack>
          </Box>
        </Box>
      </Modal>
      <Dialog
        open={confirmOpen}
        onClose={handleCancelLogout}
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogContent>
          <DialogContentText>
            Are you sure you want to logout?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelLogout}
            variant="outlined"
            sx={{
              background: " #f7f1f1",
              color: "#000",
              borderColor: "#000",
            }}
          >
            No
          </Button>
          <Button
            onClick={handleConfirmLogout}
            variant="contained"
            autoFocus
            sx={{ background: "#1d5c34" }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      {/* Bottom Navigation
      <BottomBar /> */}
    </div>
  );
}
